import type { BackendModule, ReadCallback } from 'i18next';
import enTranslation from '../locales/en/translation.json';
import daTranslation from '../locales/da/translation.json';
import esTranslation from '../locales/es/translation.json';
import frTranslation from '../locales/fr/translation.json';
import jaTranslation from '../locales/ja/translation.json';
import nlTranslation from '../locales/nl/translation.json';
import ptBRTranslation from '../locales/ptBR/translation.json';
import trTranslation from '../locales/tr/translation.json';
import itTranslation from '../locales/it/translation.json';
import { buildVaultiscanHeaders, getApiBaseUrl } from '../api/requestUtils';
import { ExtendedAccountResponse } from '../store/types';
import store from '../store';

// Map language code to local translation
const localTranslations: Record<string, any> = {
  en: enTranslation,
  da: daTranslation,
  es: esTranslation,
  fr: frTranslation,
  ja: jaTranslation,
  nl: nlTranslation,
  ptBR: ptBRTranslation,
  tr: trTranslation,
  it: itTranslation,
};

interface CustomBackendOptions {
  customerId: string;
}

// Add translation cache
let translationCache: Record<string, any> = {};
let shouldRefresh = false;

const CustomBackend: BackendModule<CustomBackendOptions> & {
  clearCache?: () => void;
  forceRefresh?: () => void;
} = {
  type: 'backend',

  init(services, backendOptions, i18nextOptions) {
    // No-op
  },

  clearCache() {
    translationCache = {};
    shouldRefresh = true;
  },

  forceRefresh() {
    shouldRefresh = true;
  },

  async read(language, namespace, callback: ReadCallback) {
    const state = store.getState();
    const account = state.auth.account as ExtendedAccountResponse | null;
    const customerId = account?.customer_id;
    const preferredLanguage = account?.preferred_language;

    if (!customerId) {
      callback(null, localTranslations[language] || localTranslations['en']);
      return;
    }

    // Check if we have a cached version and it's not being forced to refresh
    const cacheKey = `${customerId}:${preferredLanguage}`;
    if (translationCache[cacheKey] && !shouldRefresh) {
      callback(null, translationCache[cacheKey]);
      return;
    }

    try {
      const baseUrl = getApiBaseUrl();
      const headers = buildVaultiscanHeaders({
        customerId: String(customerId),
      });

      const response = await fetch(`${baseUrl}/api/translations/${customerId}/${preferredLanguage}`, {
        headers,
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.translation?.translation && Object.keys(data.translation.translation).length > 0) {
          // Cache the translation
          translationCache[cacheKey] = data.translation.translation;
          shouldRefresh = false; // Reset the refresh flag
          callback(null, data.translation.translation);
          return;
        }
      }
    } catch (e) {
      console.error('Translation fetch failed, using fallback.', e);
    }

    callback(null, localTranslations[preferredLanguage ?? 'en'] || localTranslations['en']);
  },
};

export default CustomBackend;
